import { Api } from '@monorepo/shared/apiClient';
import {
  AnalyticsSyncCacheEvents,
  analyticsSyncCacheRegister,
} from '@monorepo/shared/cacheRegisters/data2/analyticsSyncCacheRegister';
import { getQueryConfig } from '@monorepo/shared/utils/queryUtils';
import { queryCache, QueryConfig, useQuery } from 'react-query';

type KeyParams = Api.FetchDatasetsSyncingToAnalyticsParams;
const KEY_START = 'datasetsSyncingToAnalytics';
const createQueryKey = ({ organizationId }: KeyParams) =>
  [KEY_START, organizationId] as const;

type Fetcher = Api.DataHookQueryFn<
  typeof createQueryKey,
  typeof Api.fetchDatasetsSyncingToAnalytics
>;

export type UseDatasetsSyncingToAnalyticsParams =
  Partial<Api.FetchDatasetsSyncingToAnalyticsParams> & {
    config?: QueryConfig<
      Api.FetchDatasetsSyncingToAnalyticsResponse,
      Api.ErrorResponse
    >;
  };

const fetcher: Fetcher = (_: string, organizationId: string) =>
  Api.fetchDatasetsSyncingToAnalytics({ organizationId });

export const useDatasetsSyncingToAnalytics = ({
  config: inputConfig = { useErrorBoundary: true },
  organizationId,
}: UseDatasetsSyncingToAnalyticsParams) => {
  const isEnabled = !!organizationId;
  const config = getQueryConfig(inputConfig, isEnabled);

  const key = isEnabled ? createQueryKey({ organizationId }) : undefined;
  const { data: datasetsSyncingToAnalytics, ...queryInfo } = useQuery(
    key,
    fetcher,
    config,
  );
  return {
    datasetsSyncingToAnalytics,
    ...queryInfo,
  };
};

async function invalidateCache(keyParams: KeyParams): Promise<void> {
  await queryCache.invalidateQueries([KEY_START, keyParams.organizationId]);
}

const cacheRegisterInvalidator = {
  hookName: 'useDatasetsSyncingToAnalytics',
  callback: (keyParams: KeyParams) => invalidateCache(keyParams),
};

analyticsSyncCacheRegister(
  [AnalyticsSyncCacheEvents.CREATE, AnalyticsSyncCacheEvents.DELETE],
  cacheRegisterInvalidator,
);
